import { gql, useMutation } from '@apollo/client';
import { getTranslations } from '@assets/i18n/config';
import { WorkflowActionTypes } from '@generated/graphql';
import {
  IntegrationProviders,
  IntegrationTypes,
  LinkTokenConfig,
} from '@interfaces/integrations';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidEmbeddedLink } from 'react-plaid-link';

const CREATE_LINK_TOKEN = gql`
  mutation createLinkToken($input: CreateLinkTokenInput!) {
    createLinkToken(input: $input) {
      linkToken
    }
  }
`;

const FILE_NAME = 'netWorth';

const NetWorthIntegrationLink = ({ onNext }: { onNext: () => void }) => {
  const { t } = useTranslation(FILE_NAME);
  const { sys } = useThemeTokens();
  const { showToast } = useGlobalToast();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const { integrations } = useContext(UserContext);
  const { netWorthData, setNetWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const setApplicationContext =
    Object.keys(netWorthData).length !== 0 ? setNetWorthData : setWorkflowData;

  const findIntegrationCondition = (integration: any) =>
    integration?.type === IntegrationTypes.BANKING &&
    ['netWorthAutomaticLinkingUrl', 'netWorthAutomaticLinkingToken'].every(
      (key) => Object.keys(integration?.configuration ?? {}).includes(key),
    );

  const bankingIntegration = integrations?.find(findIntegrationCondition);
  const hasPlaidIntegration =
    bankingIntegration &&
    bankingIntegration.provider === IntegrationProviders.PLAID;
  const configurations = bankingIntegration?.configuration;

  const [createLinkToken] = useMutation(CREATE_LINK_TOKEN, {
    variables: {
      input: {
        userId: applicableData.objectId || applicableData.currentUserId,
        clientId: configurations?.customerId,
        plaidSecret: configurations?.plaidSecret,
        configType: LinkTokenConfig.NET_WORTH,
      },
    },
  });

  useEffect(() => {
    if (!hasPlaidIntegration) {
      setIsLoading(false);
      return;
    }
    const fetchLinkToken = async () => {
      const token = await createLinkToken();
      setLinkToken(token.data?.createLinkToken?.linkToken ?? null);
      setIsLoading(false);
    };
    fetchLinkToken();
  }, []);

  return (
    <>
      {!isLoading && hasPlaidIntegration && linkToken && (
        <PlaidEmbeddedLink
          token={linkToken}
          onSuccess={(public_token: string) => {
            setApplicationContext({
              ...applicableData,
              netWorthData: {
                ...applicableData.netWorthData,
                publicToken: public_token ?? '',
                netWorthStep: 2,
                netWorthLinkingActionType:
                  WorkflowActionTypes.SyncNetWorthAccountsFromPlaid,
                netWorthLinkingNotificationGroup:
                  configurations?.netWorthAutomaticLinkingNotificationGroup ??
                  '',
                notificationSuccessTitle: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationSuccessTitle',
                  ],
                }),
                notificationSuccessBody: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationSuccessBody',
                  ],
                }),
                notificationFailureTitle: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationFailureTitle',
                  ],
                }),
                notificationFailureBody: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationFailureBody',
                  ],
                }),
                notificationLinkText: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationLinkText',
                  ],
                }),
                notificationLink: '#',
              },
            });
            onNext();
          }}
          onExit={(error) => {
            if (error) {
              showToast({
                message: t('addNetWorthAutomatically.linkError'),
                severity: 'error',
              });
            }
          }}
          style={{
            height: '550px',
            width: '550px',
            border: 0,
          }}
        />
      )}
      {!isLoading && (!hasPlaidIntegration || !linkToken) && (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          height='550px'
          paddingX='40px'
        >
          <Typography variant='labelLarge' sx={{ mb: 3 }}>
            {t('addNetWorthAutomatically.noIntegration')}
          </Typography>
        </Box>
      )}
      {isLoading && (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          height='550px'
          paddingX='40px'
        >
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            marginBottom='40px'
          >
            <CircularProgress style={{ marginBottom: sys.spacing.xl }} />
            <Typography variant='labelLarge' sx={{ mb: 3 }}>
              {t('addNetWorthAutomatically.linkingTimeInfo')}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NetWorthIntegrationLink;
